/**
 * Foto-Tagebuch: alle Fotos einer Pflanze als Zeitstrahl.
 * „Zeitraffer“ spielt die Fotos nacheinander ab – so sieht man das Wachstum.
 */
import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { fmtDateShort, type Photo } from '@pflanzen/shared';
import { usePhoto } from '../lib/hooks';
import { Sheet } from './kit';

function Thumb({ photo, tz, onOpen }: { photo: Photo; tz: string; onOpen: () => void }) {
  const url = usePhoto(photo.id);
  return (
    <button type="button" onClick={onOpen} className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-moss-soft">
      {url && <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />}
      <span className="absolute bottom-1 left-1 rounded-full bg-black/45 px-1.5 py-0.5 text-[10px] font-semibold text-white">
        {fmtDateShort(new Date(photo.takenAt), tz)}
      </span>
    </button>
  );
}

function Viewer({ photos, start, tz, onClose }: { photos: Photo[]; start: number; tz: string; onClose: () => void }) {
  const [index, setIndex] = useState(start);
  const [playing, setPlaying] = useState(false);
  const photo = photos[index];
  const url = usePhoto(photo?.id);
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % photos.length), 700);
    return () => clearInterval(t);
  }, [playing, photos.length]);
  if (!photo) return null;
  return (
    <Sheet open onClose={onClose} title={fmtDateShort(new Date(photo.takenAt), tz)}>
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-3xl bg-black">
        {url && <img src={url} alt="" className="h-full w-full object-contain" />}
      </div>
      <input
        type="range"
        min={0}
        max={photos.length - 1}
        value={index}
        onChange={(e) => setIndex(Number(e.target.value))}
        className="mt-4 w-full accent-moss"
        aria-label="Foto wählen"
      />
      <div className="mt-2 mb-2 flex items-center justify-between text-sm text-muted">
        <span>
          Foto {index + 1} von {photos.length}
        </span>
        {photos.length > 1 && (
          <button type="button" onClick={() => setPlaying(!playing)} className="flex min-h-10 items-center gap-1.5 rounded-full bg-moss px-4 font-semibold text-paper">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {playing ? 'Stopp' : 'Zeitraffer'}
          </button>
        )}
      </div>
    </Sheet>
  );
}

export function PhotoDiary({ photos, plantName, tz }: { photos: Photo[]; plantName: string; tz: string }) {
  const [open, setOpen] = useState<number | null>(null);
  if (photos.length === 0) {
    return (
      <p className="rounded-3xl border border-dashed border-line p-4 text-sm text-muted">
        Noch keine Fotos von {plantName}. Tipp: nach dem Gießen ab und zu ein Foto aus derselben Position – daraus wird ein Zeitraffer.
      </p>
    );
  }
  const recent = [...photos].reverse().slice(0, 9);
  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {recent.map((p) => (
          <Thumb key={p.id} photo={p} tz={tz} onOpen={() => setOpen(photos.indexOf(p))} />
        ))}
      </div>
      {open != null && <Viewer photos={photos} start={open} tz={tz} onClose={() => setOpen(null)} />}
    </>
  );
}
